/** @jsx jsx */
import { jsx } from "@emotion/react";
import { FC, Fragment, useMemo, useState } from "react";
import RelationTable from "src/components/molecules/FunctionParametersModal/RelationTable";
import NoFooterModal from "src/components/molecules/modals/NoFooterModal";
import { TemplateId, WhereItems } from "src/globalTypes";
import { useFormattedMsg } from "src/hooks/useFormatMsg";
import { useBooleanState } from "src/hooks/utils";
import { RowKey } from "src/requests/table/types";
import {
  EditRowsWithColumns,
  EditRowWithColumns,
  TableRowValueDict,
} from "src/requests/tableRowActions/types";
import { SaveButtonWithLabel } from "../EwaButtons";
import RelationInput from "./RelationInput";
import { footerContainerCss } from "./styles";

interface Props {
  name?: string;
  value: number | string | null;
  tableRowData: EditRowWithColumns | EditRowsWithColumns;
  templateId: number;
  rowValues?: TableRowValueDict;
  detailTemplateId?: TemplateId | null;
  filter?: WhereItems;
  rowKey?: RowKey;
  disabled: boolean;
  required: boolean;
  isLoading: boolean;
  autoCloseOnSelect?: boolean;

  handleClearInput(e: React.MouseEvent<Element>): void;
  handleRelationSelect(tableRow: EditRowWithColumns): void;
  handleOnModalClose?(): void;
}

const RelationInputWithTable: FC<Props> = ({
  name,
  value,
  tableRowData,
  templateId,
  detailTemplateId,
  filter,
  rowKey,
  rowValues,
  disabled,
  required,
  isLoading,
  autoCloseOnSelect = false,
  handleClearInput,
  handleRelationSelect,
  handleOnModalClose,
}) => {
  const modalTitleMsg = useFormattedMsg("table.chose_item");
  const [isModalOpen, toggleModal] = useBooleanState(false);
  const [searchText, setSearchText] = useState("");

  const getSelectedRowKeys = () => {
    if (!Array.isArray(tableRowData)) return undefined;

    const selectedRowKeys = new Set<RowKey>();

    tableRowData.forEach(({ row }) => selectedRowKeys.add(row.keyValue));

    return selectedRowKeys;
  };

  const selectedRowKeys = useMemo(getSelectedRowKeys, [tableRowData]);

  const closeModal = (): void => {
    if (handleOnModalClose) handleOnModalClose();
    toggleModal();
  };

  const handleRelationSelectWithAutoClose = (
    tableRow: EditRowWithColumns
  ): void => {
    handleRelationSelect(tableRow);
    if (autoCloseOnSelect) closeModal();
  };

  const handleSearchTextChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setSearchText(e.target.value);

  const clearSearchText = () => {
    setSearchText("");
  };

  return (
    <Fragment>
      <RelationInput
        name={name}
        rowData={Array.isArray(tableRowData) ? tableRowData : [tableRowData]}
        disabled={disabled}
        toggleModal={toggleModal}
        isLoading={isLoading}
        required={required}
        value={value}
        modalIsOpen={isModalOpen}
        clearData={handleClearInput}
        onChange={handleSearchTextChange}
        searchText={searchText}
        clearSearchText={clearSearchText}
      />
      <NoFooterModal
        isOpen={isModalOpen}
        title={modalTitleMsg}
        toggle={closeModal}
      >
        <RelationTable
          templateId={templateId}
          onSelectCallback={handleRelationSelectWithAutoClose}
          rowValues={rowValues}
          detailTemplateId={detailTemplateId}
          filter={filter}
          rowKey={rowKey}
          keysOfSelectedRows={selectedRowKeys}
          searchTextValue={searchText}
        />
        {handleOnModalClose && (
          <div css={footerContainerCss}>
            <SaveButtonWithLabel onClick={closeModal} />
          </div>
        )}
      </NoFooterModal>
    </Fragment>
  );
};

export default RelationInputWithTable;
